const MAX_PHOTO_DIMENSION = 2048
const JPEG_QUALITY = 0.82

function loadImage(file: File): Promise<{ image: HTMLImageElement; objectUrl: string }> {
  const objectUrl = URL.createObjectURL(file)

  return new Promise((resolve, reject) => {
    const image = new Image()

    image.onload = () => resolve({ image, objectUrl })
    image.onerror = () => {
      URL.revokeObjectURL(objectUrl)
      reject(new Error(`Unable to decode image: ${file.name}`))
    }
    image.src = objectUrl
  })
}

function canvasToJpeg(canvas: HTMLCanvasElement): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      blob => blob ? resolve(blob) : reject(new Error('Unable to resize image')),
      'image/jpeg',
      JPEG_QUALITY
    )
  })
}

/**
 * Reduce camera photos before they enter the durable offline queue. GIFs are
 * left untouched because drawing them to a canvas would discard animation.
 */
export async function preparePhotoForUpload(file: File): Promise<File> {
  if (file.type === 'image/gif') return file

  const { image, objectUrl } = await loadImage(file)

  try {
    const scale = Math.min(
      1,
      MAX_PHOTO_DIMENSION / Math.max(image.naturalWidth, image.naturalHeight)
    )
    const width = Math.max(1, Math.round(image.naturalWidth * scale))
    const height = Math.max(1, Math.round(image.naturalHeight * scale))
    const canvas = document.createElement('canvas')
    canvas.width = width
    canvas.height = height

    const context = canvas.getContext('2d')
    if (!context) throw new Error('Unable to create image canvas')

    // JPEG has no transparency; use white rather than turning transparent PNG
    // pixels black when normalising selected photos to JPEG.
    context.fillStyle = '#ffffff'
    context.fillRect(0, 0, width, height)
    context.drawImage(image, 0, 0, width, height)

    const resizedBlob = await canvasToJpeg(canvas)

    // Avoid replacing an already-small image with a larger recompressed copy.
    if (scale === 1 && resizedBlob.size >= file.size) return file

    const baseName = file.name.replace(/\.[^.]+$/, '') || 'photo'
    return new File([resizedBlob], `${baseName}.jpg`, {
      type: 'image/jpeg',
      lastModified: file.lastModified
    })
  } finally {
    URL.revokeObjectURL(objectUrl)
  }
}
